import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { pb } from '../lib/pocketbase.js';
import AktivitaetFormModal from './AktivitaetFormModal.jsx';

// Globaler Provider für das Aktivitäts-Modal.
// Damit kann jede Komponente im Baum (z. B. ein Telefon-Link in der Praxen-
// Tabelle) das Modal vorausgefüllt öffnen, ohne lokales State-Management.

const Ctx = createContext(null);

const CHANGE_EVENT = 'aktivitaet-saved';

/** Wird nach einem Save aus dem Modal ausgelöst — Listen können darauf reagieren. */
export function dispatchAktivitaetChange() {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/** Lauscht auf Aktivitäts-Save-Events und ruft den Callback auf. */
export function useAktivitaetChangeListener(callback) {
  useEffect(() => {
    window.addEventListener(CHANGE_EVENT, callback);
    return () => window.removeEventListener(CHANGE_EVENT, callback);
  }, [callback]);
}

async function loadKontakteForPraxis(praxisId) {
  if (!praxisId) return [];
  try {
    return await pb.collection('kontakte').getFullList({
      filter: `praxis = "${praxisId}"`,
      sort: 'nachname,vorname',
    });
  } catch {
    return [];
  }
}

export function AktivitaetModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [praxisId, setPraxisId] = useState(null);
  const [aktivitaet, setAktivitaet] = useState(null);
  const [kontakte, setKontakte] = useState([]);
  const [defaultValues, setDefaultValues] = useState({});
  // Optionaler Callback, der nach erfolgreichem Save zusätzlich zum
  // Window-Event mit dem gespeicherten Record aufgerufen wird. Wird in
  // useRef gehalten, damit ein Re-Render des Aufrufers nicht das Modal
  // schließt — das Modal-Lebenszyklus bleibt unabhängig vom Aufrufer.
  const onSavedRef = useRef(null);

  const openCreate = useCallback(
    async ({
      praxisId: pId,
      prefill = {},
      kontakte: providedKontakte,
      onSaved,
    } = {}) => {
      if (!pId) return;
      setPraxisId(pId);
      setAktivitaet(null);
      setDefaultValues(prefill);
      onSavedRef.current = typeof onSaved === 'function' ? onSaved : null;
      setKontakte(providedKontakte ?? (await loadKontakteForPraxis(pId)));
      setOpen(true);
    },
    []
  );

  const openEdit = useCallback(
    async ({ aktivitaet: act, kontakte: providedKontakte, onSaved } = {}) => {
      if (!act) return;
      setPraxisId(act.praxis);
      setAktivitaet(act);
      setDefaultValues({});
      onSavedRef.current = typeof onSaved === 'function' ? onSaved : null;
      setKontakte(providedKontakte ?? (await loadKontakteForPraxis(act.praxis)));
      setOpen(true);
    },
    []
  );

  const handleSaved = useCallback((saved) => {
    setOpen(false);
    dispatchAktivitaetChange();
    const cb = onSavedRef.current;
    onSavedRef.current = null;
    if (cb) cb(saved);
  }, []);

  const handleClose = useCallback(() => {
    // Wenn der User abbricht, darf der Aufrufer-Callback nicht feuern —
    // sonst würde z.B. die Selektion fälschlich als erledigt markiert.
    onSavedRef.current = null;
    setOpen(false);
  }, []);

  const value = useMemo(() => ({ openCreate, openEdit }), [openCreate, openEdit]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <AktivitaetFormModal
        open={open}
        onClose={handleClose}
        praxisId={praxisId}
        aktivitaet={aktivitaet}
        kontakte={kontakte}
        defaultValues={defaultValues}
        onSaved={handleSaved}
      />
    </Ctx.Provider>
  );
}

export function useAktivitaetModal() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useAktivitaetModal muss innerhalb von <AktivitaetModalProvider> verwendet werden');
  }
  return ctx;
}
